import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";

// MUI
import { Card, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";

// Layout
import PageLayout from "examples/LayoutContainers/PageLayout";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// API
import { fetchAPIService } from "redux/ApiService/ApiService";

// Assets
import bgImage from "assets/images/bgLogin.jpg";
import logo from "assets/images/loginLogo.png";

// Constants
import { Colors } from "utils/Constants";
import pxToRem from "assets/theme/functions/pxToRem";

const FILE_TYPE_LABEL = { pdf: "PDF", xlsx: "Excel", xls: "Excel", zip: "ZIP" };

function decodeTokenPayload(token) {
  try {
    const [encodedPayload] = (token || "").split(".");
    if (!encodedPayload) return null;
    const padded = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

function getFileType(fileName) {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  return FILE_TYPE_LABEL[ext] || "FILE";
}

function formatFileSize(bytes) {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return "—";
  return new Date(expiresAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const ROW_STYLE = { borderBottom: "1px solid #e5e7eb" };

const LABEL_CELL = {
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: pxToRem(13),
  fontWeight: 700,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  width: "30%",
  verticalAlign: "middle",
};

const VALUE_CELL = {
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  fontSize: pxToRem(14),
  color: "#111827",
  wordBreak: "break-all",
  verticalAlign: "middle",
};

function InfoRow({ label, value, shaded }) {
  const bg = shaded ? "#f9fafb" : "#fff";
  return (
    <tr style={ROW_STYLE}>
      <td style={{ ...LABEL_CELL, backgroundColor: bg }}>{label}</td>
      <td style={{ ...VALUE_CELL, backgroundColor: bg }}>{value}</td>
    </tr>
  );
}

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  shaded: PropTypes.bool,
};

InfoRow.defaultProps = {
  value: "—",
  shaded: false,
};

function SecureDownload() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [pageState, setPageState] = useState("loading");
  const [tokenData, setTokenData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setPageState("invalid");
      return;
    }
    const payload = decodeTokenPayload(token);
    if (!payload || !payload.fileName || !payload.expiresAt) {
      setPageState("invalid");
      return;
    }
    if (Date.now() > payload.expiresAt) {
      setTokenData(payload);
      setPageState("expired");
      return;
    }
    setTokenData(payload);
    setPageState("ready");
  }, [token]);

  const handleDownload = async () => {
    setPageState("downloading");
    try {
      const response = await fetchAPIService(`/exports/authenticated-download/${token}`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = tokenData?.fileName || "download";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
      setPageState("ready");
    } catch (err) {
      if (tokenData?.expiresAt && Date.now() > tokenData.expiresAt) {
        setPageState("expired");
        return;
      }
      setErrorMessage(err?.message || "Download failed. Please try again.");
      setPageState("error");
    }
  };

  const renderBody = () => {
    if (pageState === "loading") {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={pxToRem(120)}>
          <CircularProgress sx={{ color: Colors.PRIMARY }} />
        </MDBox>
      );
    }

    if (pageState === "invalid") {
      return (
        <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
          <ErrorOutlineIcon sx={{ fontSize: pxToRem(44), color: Colors.ERROR }} />
          <MDTypography variant="h6" fontWeight="bold" color="error" textAlign="center">
            Invalid Download Link
          </MDTypography>
          <MDTypography variant="body2" color="text" textAlign="center">
            This link is invalid or has been tampered with. Please request a new export.
          </MDTypography>
        </MDBox>
      );
    }

    if (pageState === "expired") {
      return (
        <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
          <ErrorOutlineIcon sx={{ fontSize: pxToRem(44), color: Colors.WARNING }} />
          <MDTypography variant="h6" fontWeight="bold" textAlign="center">
            Download Link Expired
          </MDTypography>
          <MDTypography variant="body2" color="text" textAlign="center">
            This link expired on {formatExpiry(tokenData?.expiresAt)}. Please generate a new export.
          </MDTypography>
        </MDBox>
      );
    }

    if (pageState === "error") {
      return (
        <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
          <ErrorOutlineIcon sx={{ fontSize: pxToRem(44), color: Colors.ERROR }} />
          <MDTypography variant="h6" fontWeight="bold" color="error" textAlign="center">
            Download Failed
          </MDTypography>
          <MDTypography variant="body2" color="text" textAlign="center">
            {errorMessage}
          </MDTypography>
          <MDButton
            variant="gradient"
            sx={{
              backgroundColor: Colors.PRIMARY,
              color: "#fff",
              textTransform: "capitalize",
              mt: 1,
            }}
            onClick={() => setPageState("ready")}
          >
            Try Again
          </MDButton>
        </MDBox>
      );
    }

    const isDownloading = pageState === "downloading";
    const fileType = getFileType(tokenData?.fileName);

    return (
      <MDBox display="flex" flexDirection="column" gap={2.5}>
        <MDTypography variant="body2" color="text">
          Your export is ready. Click the button below to download.
        </MDTypography>

        <MDBox
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #e5e7eb",
            borderRadius: pxToRem(6),
            overflow: "hidden",
          }}
        >
          <tbody>
            <InfoRow label="File Name" value={tokenData?.fileName} shaded />
            <InfoRow label="File Type" value={fileType} />
            {formatFileSize(tokenData?.fileSize) && (
              <InfoRow label="File Size" value={formatFileSize(tokenData?.fileSize)} shaded />
            )}
            <InfoRow label="Expires" value={formatExpiry(tokenData?.expiresAt)} />
          </tbody>
        </MDBox>

        <MDBox display="flex" justifyContent="center" gap={2} mt={1}>
          <MDButton
            variant="gradient"
            startIcon={
              isDownloading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={isDownloading}
            sx={{
              backgroundColor: Colors.PRIMARY,
              color: "#fff",
              textTransform: "capitalize",
              fontSize: pxToRem(14),
              fontWeight: 700,
              padding: `${pxToRem(10)} ${pxToRem(32)}`,
            }}
            onClick={handleDownload}
          >
            {isDownloading ? "Downloading..." : "Download File"}
          </MDButton>
          <MDButton
            variant="outlined"
            startIcon={<HomeOutlinedIcon />}
            disabled={isDownloading}
            sx={{
              borderColor: Colors.PRIMARY,
              color: Colors.PRIMARY,
              textTransform: "capitalize",
              fontSize: pxToRem(14),
              fontWeight: 700,
              padding: `${pxToRem(10)} ${pxToRem(32)}`,
            }}
            onClick={() => window.open("/client/setting", "_blank", "noreferrer")}
          >
            Go to Portal
          </MDButton>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <PageLayout>
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <MDBox
        width="100%"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={2}
        py={4}
        sx={{ position: "relative" }}
      >
        <Card sx={{ width: "100%", maxWidth: pxToRem(520), p: pxToRem(32) }}>
          <MDBox display="flex" justifyContent="center" mb={3}>
            <MDBox component="img" src={logo} alt="Reynard" width="7rem" />
          </MDBox>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            sx={{ color: Colors.PRIMARY, mb: 3 }}
          >
            Secure Download
          </MDTypography>
          {renderBody()}
        </Card>
      </MDBox>
    </PageLayout>
  );
}

export default SecureDownload;
